import React from 'react';
import { Stack, List, ListItem } from '@mui/material';
import { getAnimationStyle } from './AnimationStyleHelper';
import { featureIconsStyles as classes } from '../styleObjects/FeatureIcons';
import { ParseAbbreviations } from '../../ParseAbbreviations';
import { getLinkTargetValue } from './SectionComponentHelper';
import { CMSLink } from '@components/CMSLink';
import { isPreviewingAnimationAtom } from '@atoms/animation';
import { useAtom } from 'jotai';
import { Heading } from '@web-for-marketing/react-ui';
import { Picture } from '@components/Picture';
import { LargeStackFeature } from '@models/section/featuresSection';
import { TextAlignValues } from '@models/editorForm';
import { RichTextContent } from '@components/RichTextContent';
import { SectionAnimation } from '@models/section';

interface LargeStackItemProps {
    animations: SectionAnimation;
    lazyLoad: boolean;
    alignFeatures: TextAlignValues;
    feature: LargeStackFeature;
    shouldIncreaseHeadingLevel: boolean;
}

export function LargeStackItem({
    feature,
    animations,
    alignFeatures,
    lazyLoad,
    shouldIncreaseHeadingLevel,
}: LargeStackItemProps): React.ReactElement {
    const [isPreviewingAnimation] = useAtom(isPreviewingAnimationAtom);
    const { image, title, body, list, button } = feature;

    return (
        <Stack gap={{ xs: '1rem', md: '3rem' }} direction={{ xs: 'column', md: 'row' }} data-testid='largeStackItem'>
            <div>
                <Picture
                    fullWidth={false}
                    imagePath={image.imagePath}
                    mobilePath={image.mobilePath ? image.mobilePath : ''}
                    alt={image.imageAlt}
                    mobileAlt={image.mobileAlt}
                    lazyLoad={lazyLoad}
                    css={[classes.modelMedia, classes[`${alignFeatures}Image`]]}
                />
            </div>
            <div>
                {title ? (
                    <Heading
                        variant='h3'
                        weight='regular'
                        component={shouldIncreaseHeadingLevel ? 'h2' : 'h3'}
                        color='primary'
                        css={[classes.modelTitle, classes[`${alignFeatures}Heading`]]}
                    >
                        <ParseAbbreviations content={title} stringFormat={undefined} />
                    </Heading>
                ) : null}
                {body ? (
                    <RichTextContent
                        content={ParseAbbreviations({ content: body, stringFormat: true })}
                        css={classes[`${alignFeatures}Body`]}
                    />
                ) : null}
                {list && list.length ? (
                    <div
                        css={classes.listContainer}
                        className={`${getAnimationStyle('listAnimation', false, animations, isPreviewingAnimation)}`}
                    >
                        <List dense css={classes.lists}>
                            {list.map((value, index: number) => {
                                return (
                                    <ListItem css={classes.listItems} key={index}>
                                        <ParseAbbreviations content={value} stringFormat={undefined} />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </div>
                ) : null}
                {button && button.buttonText ? (
                    <CMSLink
                        type='button'
                        href={button.buttonLinkPath}
                        aria-label={button.ariaLabel}
                        target={getLinkTargetValue(button.buttonTarget)}
                        variant={button.buttonType || 'text'}
                    >
                        {button.buttonText}
                    </CMSLink>
                ) : null}
            </div>
        </Stack>
    );
}
